/**
 * Default chain / project construction.
 *
 * The spec names a default system (spec 62) but every part of it is replaceable,
 * and the user can promote their own chain to be the default (spec 63).
 */

import { DEFAULT_SYSTEM } from '../database';
import type { ProjectFile, SystemChain } from '../types/system';
import {
  makeDefaultCables,
  DEFAULT_BYPASS,
  DEFAULT_ENGINE,
  DEFAULT_HEADPHONE,
  DEFAULT_LEVEL_MATCH,
  DEFAULT_ROOM,
  DEFAULT_SPATIAL,
} from '../types/system';

export const ROOM_PRESETS = {
  small: { widthM: 3.4, lengthM: 4.4, heightM: 2.4, speakerSpacingM: 1.9, listenerDistanceM: 2.1, frontWallM: 0.5, rearWallM: 0.9, absorptionWalls: 0.22, absorptionFloor: 0.14, absorptionCeiling: 0.18, rt60: 0.32 },
  medium: { widthM: 4.2, lengthM: 6.0, heightM: 2.6, speakerSpacingM: 2.4, listenerDistanceM: 2.8, frontWallM: 0.9, rearWallM: 1.4, absorptionWalls: 0.28, absorptionFloor: 0.18, absorptionCeiling: 0.22, rt60: 0.42 },
  large: { widthM: 6.0, lengthM: 9.0, heightM: 3.2, speakerSpacingM: 3.2, listenerDistanceM: 4.0, frontWallM: 1.4, rearWallM: 2.2, absorptionWalls: 0.34, absorptionFloor: 0.24, absorptionCeiling: 0.28, rt60: 0.55 },
} as const;

export function makeDefaultChain(id: string, name = 'System A'): SystemChain {
  return {
    id,
    name,
    dacId: DEFAULT_SYSTEM.dacId,
    ampId: DEFAULT_SYSTEM.ampId,
    outputId: DEFAULT_SYSTEM.outputId,
    dac: { filterId: undefined, trimDb: 0, balanced: true },
    amp: { volume: 0.5, trimDb: 0, balancedInput: true },
    room: { ...DEFAULT_ROOM },
    headphone: { ...DEFAULT_HEADPHONE, eq: [] },
    spatial: { ...DEFAULT_SPATIAL },
    bypass: { ...DEFAULT_BYPASS },
    cables: makeDefaultCables(),
    outputTrimDb: 0,
  };
}

export function makeDefaultProject(): ProjectFile {
  const now = new Date().toISOString();
  return {
    formatVersion: 1,
    name: 'Untitled project',
    created: now,
    modified: now,
    systemA: makeDefaultChain('A', 'System A'),
    systemB: { ...makeDefaultChain('B', 'System B') },
    engine: { ...DEFAULT_ENGINE },
    levelMatch: { ...DEFAULT_LEVEL_MATCH },
    comparison: 'single',
    banks: { dac: [null, null, null, null], amp: [null, null, null, null], output: [null, null, null, null] },
  };
}

export function applyRoomPreset(room: SystemChain['room'], preset: keyof typeof ROOM_PRESETS): SystemChain['room'] {
  return { ...room, ...ROOM_PRESETS[preset], preset };
}
